import { css } from '@emotion/core'
import parse from 'html-react-parser'
import React from 'react'

import AdGallery from '../components/ads/AdGallery'
import Body from '../components/Body'
import { Button, DemoButton } from '../components/Button'
import Divider from '../components/Divider'
import Footer from '../components/Footer'
import Grid from '../components/Grid'
import Header from '../components/Header'
import Image from '../components/Image'
import { Intro } from '../components/Intro'
import Layout from '../components/Layout'
import { Link } from '../components/Link'
import Partners from '../components/Partners'
import SEO from '../components/SEO'
import TestimonialSlider from '../components/TestimonialSlider'
import VideoGallery from '../components/VideoGallery'
import {
  breakpoints,
  homepageFeaturesGalleryBreakPoint,
  homepageFeaturesGalleryBreakPoint_Fr_De,
} from '../constants/breakPoints'
import { homepageValuesGalleryContent } from '../constants/galleryImages'
import { homepageFeaturesGalleryContent } from '../constants/galleryVideos'
import { headerHeight } from '../constants/header'
import { isDesktop, isMobile } from '../helpers/common'
import { useLanguage } from '../hooks/useLanguage'
import HeroPointerIcon from '../icons/abstract/hero-pointer.svg'
import { useTranslation } from '../lang'

const IndexPage: React.FunctionComponent = () => {
  const [tourStarted, setTourStarted] = React.useState(false)

  const getPlaceholderImageData = (): { src: string; aspectRatio: number } => {
    const availableWidth = isMobile(1200) ? window.innerWidth - 32 : 1136
    const availableHeight = typeof window !== 'undefined' ? window.innerHeight - headerHeight : 1
    const availableRatio = availableWidth / availableHeight

    if (availableRatio < 1.2) {
      return { src: 'tour-placeholder-ratio13.webp', aspectRatio: 1.3 }
    }
    if (availableRatio < 1.5) {
      return { src: 'tour-placeholder-ratio16.webp', aspectRatio: 1.6 }
    }
    if (availableRatio < 1.75) {
      return { src: 'tour-placeholder-ratio18.webp', aspectRatio: 1.8 }
    }
    if (availableRatio < 1.9) {
      return { src: 'tour-placeholder-ratio19.webp', aspectRatio: 1.9 }
    }
    return { src: 'tour-placeholder-ratio2.webp', aspectRatio: 2 }
  }

  const isEnPage = useLanguage() === 'en'

  const { t } = useTranslation()
  const pageLanguage = useLanguage()

  return (
    <Layout whiteBG>
      <SEO lang={pageLanguage} title={t('homepage.seo.title')} description={t('homepage.seo.description')} />
      <Header />
      <Body>
        {isMobile(breakpoints.small) ? (
          <Intro customBreakPoint={99999} />
        ) : (
          <>
            <div css={heroIntroStyle}>
              <h1 className="subtitle gray-text">{t('homepage.hero.subheading').toUpperCase()}</h1>
              <h2 className={`${isEnPage && isDesktop(breakpoints.large) ? 'heading1' : 'heading1 small'} center`}>
                {parse(t('homepage.hero.headline'))}
              </h2>
              <div className="subtitle center">{parse(t('homepage.hero.subtitle'))}</div>
              <DemoButton type="primary" cssStyles={buttonStyle} />
              <Link
                onClick={() =>
                  document
                    .getElementById('homepage-instant-tour-container')
                    ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
                }
                className="strong-body-1 blue-text center opacity-on-hover"
                css={heroLinkStyle}
              >
                {parse(t('homepage.hero.link.text'))}
              </Link>
              <div css={heroPointerIconStyle}>
                <HeroPointerIcon />
              </div>
            </div>

            <div id="homepage-instant-tour-container" css={instantTourContainerStyle}>
              <div
                className="gray-bordered-block with-blue-box-shadow"
                onClick={() => setTourStarted(true)}
                css={instantTourFrameStyle(getPlaceholderImageData().aspectRatio)}
              >
                <div css={instantTourOverlayStyle(tourStarted)} />
                <div css={instantTourOverlayImageStyle(tourStarted)}>
                  <Image
                    src={`product/${getPlaceholderImageData().src}`}
                    alt="airfocus welcome screen"
                    imgStyle={{ margin: 'auto', right: '0' }}
                  />
                </div>
                <Button className="black" css={instantTourButtonStyle(tourStarted)}>
                  {t('homepage.hero.button.text')}
                </Button>

                <iframe
                  src="https://capture.navattic.com/clrj18ks300200fl1aenpfa57/"
                  style={{ border: 'none', width: '100%', height: '100%', opacity: `${tourStarted ? 1 : 0}` }}
                  allow="fullscreen"
                />
              </div>
            </div>

            <Partners />
          </>
        )}

        <Divider size={8} />

        <AdGallery
          heading={t('homepage.valueGallery.headline')}
          galleryContent={homepageValuesGalleryContent}
          tabsContainerBreakPoint={!isEnPage ? breakpoints.xlarge : undefined}
          cssStyles={homepageValuesGalleryStyles(isEnPage)}
        />

        <TestimonialSlider heading={t('homepage.testimonialsBlock.headline')} />

        <h2 className="heading2 center">{parse(t('homepage.whyUsBlock.headline'))}</h2>
        <Grid rowGap={4} rowGapMobile={4}>
          <Grid columns={2} columnGap={7} css={whyUsGridStyle}>
            <div>
              <div className="section">{t('homepage.whyUsBlock.flexibility.section')}</div>
              <h3 className="heading3">{parse(t('homepage.whyUsBlock.flexibility.heading'))}</h3>
              <div className="subtitle">{parse(t('homepage.whyUsBlock.flexibility.abstract'))}</div>
            </div>
            <Image src="abstract/flexibility.webp" alt="Flexibility" />
          </Grid>
          <Grid columns={2} columnGap={7} responsiveReversed css={whyUsGridStyle}>
            <Image src="abstract/implementation.webp" alt="Fast implementation" />
            <div>
              <div className="section">{t('homepage.whyUsBlock.implementation.section')}</div>
              <h3 className="heading3">{parse(t('homepage.whyUsBlock.implementation.heading'))}</h3>
              <div className="subtitle">{parse(t('homepage.whyUsBlock.implementation.abstract'))}</div>
            </div>
          </Grid>
          <Grid columns={2} columnGap={7} css={whyUsGridStyle}>
            <div>
              <div className="section">{t('homepage.whyUsBlock.enterprise.section')}</div>
              <h3 className="heading3">{parse(t('homepage.whyUsBlock.enterprise.heading'))}</h3>
              <div className="subtitle">{parse(t('homepage.whyUsBlock.enterprise.abstract'))}</div>
            </div>
            <Image src="abstract/security.webp" alt="Enterprise-ready" />
          </Grid>
        </Grid>

        <Divider size={8} />
        <VideoGallery
          contentArray={homepageFeaturesGalleryContent}
          breakPoint={!isEnPage ? homepageFeaturesGalleryBreakPoint_Fr_De : homepageFeaturesGalleryBreakPoint}
          cssStyles={videoGalleryStyles(isEnPage)}
        />

        <div className="full-screen-relative" css={ctaBlockStyle}>
          <Image src="airfocus-logos/airfocus-logo-stars.webp" alt="airfocus logo" css={airfocusLogoStyle} />
          <h2 className="heading2 center center-on-small no-margin">{parse(t('homepage.ctaBlock'))}</h2>
          <DemoButton type="primary" cssStyles={demoButtonStyle} />
        </div>
      </Body>
      <Footer cssStyles={footerStyle} />
    </Layout>
  )
}

export default IndexPage

const heroIntroStyle = css`
  margin: 4rem 0 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const heroLinkStyle = css`
  margin: 2rem auto 0;
`

const heroPointerIconStyle = css`
  width: 8rem;
  transform: translateX(10%);

  path {
    stroke-dasharray: 500;
    stroke-dashoffset: 500;
    animation: draw 1s ease-in forwards;
    animation-delay: 1s;
  }

  .second-path {
    animation: draw 1.4s ease-out forwards;
    animation-delay: 2s;
  }

  .third-path {
    animation: draw 1.4s ease-in-out forwards;
    animation-delay: 3s;
  }

  @keyframes draw {
    to {
      stroke-dashoffset: 0;
    }
  }
`

const instantTourContainerStyle = css`
  margin-top: -${headerHeight}px;
  padding-top: ${headerHeight}px;
`

const instantTourFrameStyle = (aspectRatio: number) => css`
  cursor: pointer;
  position: relative;
  margin: 0 auto 8rem;
  aspect-ratio: ${aspectRatio};

  &:hover {
    .black {
      transform: translate(-50%, -50%) scale(1.05);
      color: #fff;
      background-color: #000;
    }
  }
`

const instantTourOverlayStyle = (tourStarted: boolean) => css`
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(-45deg, #bd97ff70, #79e0ff70, #318eff70);
  background-size: 400% 400%;
  animation: gradient 10s ease infinite;
  opacity: ${tourStarted ? 0 : 1};
  ${tourStarted && 'transform: scale(0)'};
  transition: all 0.5s ease;

  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`

const instantTourOverlayImageStyle = (tourStarted: boolean) => css`
  z-index: 1;
  position: absolute;
  top: 50%;
  width: 100%;
  opacity: ${tourStarted ? 0 : 1};
  transform: ${tourStarted ? 'translateY(-50%) scale(0)' : 'translateY(-50%)'};
  transition: all 0.5s ease;
`

const instantTourButtonStyle = (tourStarted: boolean) => css`
  position: absolute;
  z-index: 3;
  top: 50%;
  left: 50%;
  background: white;
  color: black;
  box-shadow: 2px 2px 55px rgba(69, 118, 255, 0.7);
  opacity: ${tourStarted ? 0 : 1};
  transform: ${tourStarted ? 'translate(-50%, -50%) scale(0)' : 'translate(-50%, -50%)'};
`

const buttonStyle = css`
  margin-top: 1.25rem;
`

// export for AdPageTemplate2025.tsx
export const homepageValuesGalleryStyles = (isEnPage: boolean) => css`
  .views-dropdown-container {
    max-width: 34rem;
  }

  ${!isEnPage &&
  `
    .ad-gallery-content {
      @media screen and (min-width: ${breakpoints.medium + 1}px) {
        text-align: left;
      }
    }
  `}

  @media screen and (min-width: ${breakpoints.small + 1}px) {
    .ad-gallery-tabs-container {
      justify-content: space-between;

      .ad-gallery-tab {
        margin-right: 0 !important;
      }
    }
  }

  & > div {
    align-items: center;
    grid-template-columns: 1fr 2fr;

    @media screen and (max-width: ${breakpoints.medium}px) {
      grid-template-columns: 1fr;
    }
  }

  @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${breakpoints.medium}px) {
    .gatsby-image-wrapper {
      width: 500px;
      margin: auto;
    }
  }
`

// export for AdPageTemplate2025.tsx
export const whyUsGridStyle = css`
  @media screen and (max-width: ${breakpoints.large}px) {
    column-gap: 2rem;
  }
`

const videoGalleryStyles = (isEnPage: boolean) => css`
  & > div > div > div {
    max-width: 60rem;
  }

  @media screen and (min-width: ${isEnPage
      ? homepageFeaturesGalleryBreakPoint + 1
      : homepageFeaturesGalleryBreakPoint_Fr_De}px) {
    .video-gallery-block {
      grid-template-columns: 310px 1fr;
    }
  }

  @media screen and (min-width: ${breakpoints.medium + 1}px) and (max-width: ${isEnPage
      ? homepageFeaturesGalleryBreakPoint
      : homepageFeaturesGalleryBreakPoint_Fr_De}px) {
    .video-gallery-block-mobile {
      grid-template-columns: 310px 1fr;
    }
  }
`

// export for AdPageTemplate2025.tsx
export const ctaBlockStyle = css`
  position: relative;
  padding: 12rem 0 8rem;
  background: linear-gradient(rgba(243, 252, 255, 0), rgba(230, 249, 255, 100) 70%);

  .underline {
    white-space: nowrap;
    padding: 0 0 8px;
    position: relative;
    z-index: 1;

    &::before {
      content: '';
      position: absolute;
      bottom: 7px;
      left: -5px;
      width: 120%;
      height: 2px;
      border-radius: 30%;
      transform: rotate(-2deg) translateX(-3%) translateZ(20px);
      background: #318eff;
      z-index: -1;
    }
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    padding-top: 8rem;
  }
`

// export for AdPageTemplate2025.tsx
export const airfocusLogoStyle = css`
  width: 5rem;
  margin: 0 auto 2rem;
`

// export for AdPageTemplate2025.tsx
export const demoButtonStyle = css`
  margin: 3rem auto 0;
  display: flex;
  justify-content: center;
  width: fit-content;
`

const footerStyle = css`
  & > .full-screen-relative:first-child {
    margin-top: 0;
  }
`
